import { Col, ColorPicker, Flex, Form, Input, message, Row, Upload } from "antd";
import { FormWrapper, StyledButton } from "../../components/styles";
import { Description, GeneralSettingsContainer, MainFormWrapper, Title } from "./styles"
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { getEstablishment, updateEstablishment } from "../../redux/features/user/userAsyncThunk";
import type { ColorPickerProps, GetProp, UploadFile } from "antd";
import { StyledPhoneInput } from "../ReportDetails/Modal/styles";
import { CustomUploadFile, imageUploadAccept, validateUploadFiles } from "../../utils/helpers/validateUploadFiles";
import { UploadFileStatus } from "antd/es/upload/interface";

type Color = Extract<GetProp<ColorPickerProps, "value">, string | { cleared: any }>;

const GeneralSetting: React.FC = () => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [file, setFile] = useState<UploadFile | null>(null);
    const [reportLogoFile, setReportLogoFile] = useState<UploadFile | null>(null);
    const { establishment, loading } = useAppSelector((state) => state.users);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            let logo = null;
            let reportLogo = null;

            if (values?.logo) {
                await form.validateFields(['logo']);
                const hasBlob = values.logo instanceof Blob;
                if (hasBlob) {
                    logo = values?.logo;
                }
            }

            if (values?.reportLogo) {
                await form.validateFields(['reportLogo']);
                const hasBlob = values.reportLogo instanceof Blob;
                if (hasBlob) {
                    reportLogo = values?.reportLogo;
                }
            }

            const formattedValues = {
                ...values,
                logo: logo,
                reportLogo: reportLogo,
            }

            await dispatch(updateEstablishment({ formData: formattedValues })).unwrap();
        } catch (errorInfo) {
            console.error("Validation failed:", errorInfo);
        }
    };

    const handleColorChange = (colorHex: Color, fieldName: string) => {
        const color = typeof colorHex === "string" ? colorHex : colorHex?.toHexString();
        form.setFieldsValue({ [fieldName]: color });
    };

    const handleFileChange = async ({ file: changedFile }: { file: UploadFile }) => {
        setFile(changedFile || null);

        if (changedFile?.originFileObj) {
            const blob = new Blob([changedFile.originFileObj], { type: changedFile.originFileObj.type });
            form.setFieldsValue({ logo: blob });
        } else {
            form.setFieldsValue({ logo: undefined });
        }
    };

    const beforeUpload = (file: File) => {
        const isImg = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type);
        const isLt1M = file.size / 1024 / 1024 <= 1;

        if (!isImg) {
            message.error('Only image files are allowed!');
            return Upload.LIST_IGNORE; // Prevent the upload process
        }
        if (!isLt1M) {
            message.error('Image must be smaller than 1MB!');
            return Upload.LIST_IGNORE; // Prevent the upload process
        }

        return true; // Allow the upload to proceed if it's a valid image and size
    };


    const handlePreview = async (file: CustomUploadFile) => {
        if (file.url) {
            window.open(file.url, "_blank");
        }
    };

    const handleRemove = () => {
        setFile(null);
        form.setFieldsValue({ logo: undefined });
        return true;
    };

    const handleReportLogoChange = async ({ file: changedFile }: { file: UploadFile }) => {
        setReportLogoFile(changedFile || null);

        if (changedFile?.originFileObj) {
            const blob = new Blob([changedFile.originFileObj], { type: changedFile.originFileObj.type });
            form.setFieldsValue({ reportLogo: blob });
        } else {
            form.setFieldsValue({ reportLogo: undefined });
        }
    };

    const handleReportLogoRemove = () => {
        setReportLogoFile(null);
        form.setFieldsValue({ reportLogo: undefined });
        return true;
    };

    // Fetch establishment data on mount
    useEffect(() => {
        dispatch(getEstablishment());
    }, [dispatch]);

    useEffect(() => {
        if (establishment?.id) {
            form.setFieldsValue(establishment);
            establishment?.logoUrl && setFile({
                uid: establishment?.id.toString(),
                name: establishment?.logoUrl || 'Logo',
                status: 'done' as UploadFileStatus,
                url: establishment?.logoUrl,
            })
            establishment?.reportLogoUrl && setReportLogoFile({
                uid: establishment?.id.toString() + '-report',
                name: establishment?.reportLogoUrl || 'Report Logo',
                status: 'done' as UploadFileStatus,
                url: establishment?.reportLogoUrl,
            })
        }
    }, [establishment])

    return (
        <>
            <GeneralSettingsContainer>
                <Title>General Settings</Title>
                <Description>Manage company details here, including name, address, and contact information.</Description>

                <MainFormWrapper>
                    <FormWrapper form={form} layout="vertical">
                        <Row gutter={16} className="row-gap">
                            <Col span={24} md={12}>
                                <Form.Item label="Company Name" name="companyName" rules={[{ required: false, message: "Please enter your company name!" }]}>
                                    <Input
                                        placeholder="Regent Seven Seas Cruises"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col flex="auto" md={12}>
                                <Form.Item label="Contact Person" name="contactPerson" rules={[{ required: false, message: "Please enter contact person name!" }]}>
                                    <Input
                                        placeholder="Enter Your Contact Person"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item label="Address" name="address" rules={[{ required: false, message: "Please enter your address!" }]}>
                            <Input.TextArea
                                placeholder="Enter Your Address"
                                size="large"
                                rows={3}
                            />
                        </Form.Item>
                        <Row gutter={16} className="row-gap">
                            <Col span={24} md={8}>
                                <Form.Item label="Support Email" name="supportEmail" rules={[{ required: false, message: "Please enter your email address!" }, { type: "email", message: "Please enter valid email address" }]} normalize={(value) => value?.trim().toLowerCase() || ""}>
                                    <Input
                                        placeholder="Enter Support Email"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={24} md={8}>
                                <Form.Item label="Establishment Email (BCC)" name="email" rules={[{ required: false, message: "Please enter your email address!" }, { type: "email", message: "Please enter valid email address" }]} normalize={(value) => value?.trim().toLowerCase() || ""}>
                                    <Input
                                        placeholder="Enter Establishment Email"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col flex="auto" md={8}>
                                <Form.Item label="Contact Person Phone Number" name="phone" rules={[{ required: false, message: "Please enter your phone number!" }]}>
                                    <StyledPhoneInput
                                        country={"us"}
                                        inputProps={{
                                            placeholder: "Contact Person Phone Number",
                                        }}
                                        inputStyle={{
                                            width: "100%",
                                            padding: "10px",
                                            borderRadius: "3px",
                                            fontSize: "15px",
                                            paddingLeft: "40px",
                                            background: "#fff",
                                            height: "40px",
                                        }}
                                        buttonStyle={{
                                            borderRight: "none",
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className="row-gap">
                            <Col span={24} md={12}>
                                <Form.Item label="Fax" name="fax" rules={[{ required: false, message: "Please enter fax number!" }]}>
                                    <Input
                                        placeholder="Enter Your Fax"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col flex="auto" md={12}>
                                <Form.Item label="Website" name="website" rules={[{ required: false, message: "Please enter your website!" }, { type: "url", message: "Please enter a valid website URL" }]}>
                                    <Input
                                        placeholder="Enter Your Website Name"
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item label="Guest Note" name="customerNote" rules={[{ required: false, message: "Please enter a note!" }]}>
                            <Input
                                placeholder="Enter a Guest Note"
                                size="large"
                            />
                        </Form.Item>
                        <Row gutter={16} className="row-gap">
                            <Col span={24} md={12}>
                                <Form.Item label="Company Logo" name="logo">
                                    <Upload
                                        listType="picture-card"
                                        fileList={file ? [file] : []}
                                        onChange={handleFileChange}
                                        beforeUpload={beforeUpload}
                                        maxCount={1}
                                        onPreview={handlePreview}
                                        showUploadList={{
                                            showPreviewIcon: true,
                                            showRemoveIcon: false,
                                            showDownloadIcon: false,
                                        }}
                                        onRemove={handleRemove}
                                        accept={imageUploadAccept}
                                        name="logo"
                                        customRequest={({ file, onSuccess }) => {
                                            // Simulate a successful upload so AntD shows the preview
                                            setTimeout(() => {
                                                onSuccess?.("ok");
                                            }, 0);
                                        }}
                                    >
                                        + Upload
                                    </Upload>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={12}>
                                <Form.Item label="Report Logo (PDF Documents)" name="reportLogo">
                                    <Upload
                                        listType="picture-card"
                                        fileList={reportLogoFile ? [reportLogoFile] : []}
                                        onChange={handleReportLogoChange}
                                        beforeUpload={beforeUpload}
                                        maxCount={1}
                                        onPreview={handlePreview}
                                        showUploadList={{
                                            showPreviewIcon: true,
                                            showRemoveIcon: false,
                                            showDownloadIcon: false,
                                        }}
                                        onRemove={handleReportLogoRemove}
                                        accept={imageUploadAccept}
                                        name="reportLogo"
                                        customRequest={({ file, onSuccess }) => {
                                            // Simulate a successful upload so AntD shows the preview
                                            setTimeout(() => {
                                                onSuccess?.("ok");
                                            }, 0);
                                        }}
                                    >
                                        + Upload
                                    </Upload>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16} className="row-gap">
                            <Col span={24} md={5} >
                                <Form.Item label="Light Theme Color(SendGrid)" name="templateThemeLight" initialValue="#1677ff" rules={[{ required: true, message: "Please select email template accent color." }]}>
                                    <ColorPicker defaultValue="#1677ff" size="large" onChange={(color) => handleColorChange(color, "templateThemeLight")} />
                                </Form.Item>
                            </Col>
                            <Col span={24} md={5}>
                                <Form.Item label="Dark Theme Color(SendGrid)" name="templateThemeDark" initialValue="#022350" rules={[{ required: true, message: "Please select email template accent color." }]}>
                                    <ColorPicker defaultValue="#022350" size="large" onChange={(color) => handleColorChange(color, "templateThemeDark")} />
                                </Form.Item>
                            </Col>
                            <Col span={24} md={4}>
                                <Form.Item label="Primary Color" name="primaryColor" initialValue="#a07832" rules={[{ required: true, message: "Please select primary color." }]}>
                                    <ColorPicker defaultValue="#a07832" size="large" onChange={(color) => handleColorChange(color, "primaryColor")} />
                                </Form.Item>
                            </Col>
                            <Col span={24} md={4}>
                                <Form.Item label="Secondary Color" name="secondaryColor" initialValue="#ffedcf" rules={[{ required: true, message: "Please select secondary color." }]}>
                                    <ColorPicker defaultValue="#ffedcf" size="large" onChange={(color) => handleColorChange(color, "secondaryColor")} />
                                </Form.Item>
                            </Col>
                            <Col flex="auto" md={4}>
                                <Form.Item label="Background Color" name="bgColor" initialValue="#444444" rules={[{ required: true, message: "Please select background color." }]}>
                                    <ColorPicker defaultValue="#444444" size="large" onChange={(color) => handleColorChange(color, "bgColor")} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </FormWrapper>

                </MainFormWrapper>
                <Flex justify="flex-end">
                    <StyledButton
                        type="primary"
                        onClick={handleSave}
                        loading={loading}
                    >
                        Save Settings
                    </StyledButton>
                </Flex>
            </GeneralSettingsContainer>
        </>
    )
}

export default GeneralSetting
